/**
 * Pure helpers for wizard Step 3 assignable-assets query keys / URL params.
 * Kept free of OpenAPI client imports so node:test can import them directly.
 */

export type WizardAssignableAssetFilters = {
  organizationIds?: string[];
  assetTypeIds?: string[];
  assetModelIds?: string[];
  landlordOrganizationIds?: string[];
  siteIds?: string[];
};

export type WizardAssignableAssetsPagination = {
  page: number;
  limit: number;
};

export const WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE = 8;

function hasNonEmptyIds(ids?: string[]): ids is string[] {
  return Array.isArray(ids) && ids.length > 0;
}

function appendIdParams(
  params: URLSearchParams,
  key: string,
  ids?: string[],
): void {
  for (const id of ids ?? []) {
    if (id) params.append(key, id);
  }
}

function serializeIdList(ids: string[] | undefined): string {
  if (!hasNonEmptyIds(ids)) return '';
  return [...ids].sort().join(',');
}

/** Build query string for wizard assignable-assets (filters + pagination). */
export function buildWizardAssignableAssetsSearchParams(
  filters?: WizardAssignableAssetFilters,
  pagination: WizardAssignableAssetsPagination = {
    page: 1,
    limit: WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
  },
): URLSearchParams {
  const params = new URLSearchParams();
  params.set('page', String(pagination.page));
  params.set('limit', String(pagination.limit));
  appendIdParams(params, 'organizationIds', filters?.organizationIds);
  appendIdParams(params, 'assetTypeIds', filters?.assetTypeIds);
  appendIdParams(params, 'assetModelIds', filters?.assetModelIds);
  appendIdParams(
    params,
    'landlordOrganizationIds',
    filters?.landlordOrganizationIds,
  );
  appendIdParams(params, 'siteIds', filters?.siteIds);
  return params;
}

/**
 * React Query key for wizard assignable-assets.
 * Any multi-select dimension or page change must alter this key so a fetch runs.
 */
export function buildWizardAssignableAssetsQueryKey(
  organizationScopeKey: string,
  filters: WizardAssignableAssetFilters = {},
  pagination: WizardAssignableAssetsPagination = {
    page: 1,
    limit: WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
  },
): readonly [
  'preventive-maintenance',
  'wizard-assignable-assets',
  string,
  string,
  string,
  string,
  string,
  string,
  number,
  number,
] {
  return [
    'preventive-maintenance',
    'wizard-assignable-assets',
    organizationScopeKey,
    serializeIdList(filters.organizationIds),
    serializeIdList(filters.assetTypeIds),
    serializeIdList(filters.assetModelIds),
    serializeIdList(filters.landlordOrganizationIds),
    serializeIdList(filters.siteIds),
    pagination.page,
    pagination.limit,
  ] as const;
}

export function hasAssignableAssetFilters(
  filters?: WizardAssignableAssetFilters,
): boolean {
  if (!filters) return false;
  return (
    hasNonEmptyIds(filters.organizationIds) ||
    hasNonEmptyIds(filters.assetTypeIds) ||
    hasNonEmptyIds(filters.assetModelIds) ||
    hasNonEmptyIds(filters.landlordOrganizationIds) ||
    hasNonEmptyIds(filters.siteIds)
  );
}
